// frontend/src/services/estimationCalc.js
//
// Hierarchy maths for the Kostenschätzung table.
//
// Behaviour is identical to the previous implementation inside
// CostEstimation.jsx; only the cost changed. The old version rescanned the
// whole row array for every row (findIndex / some / filter per row, twice
// over), which is quadratic: ~0.33 M comparisons for a 374-row sheet and
// ~11.9 M for a 2183-row one, on every single cell edit. Here the parent and
// code lookups are indexed once per recalculation, which makes it linear.
//
// The second change is that recalculation no longer mutates the row objects
// in place. Rows whose values did not change keep their identity, so the
// table can skip re-rendering them (see shouldCellUpdate in EstimationTable).

import { calculateRowTotal, inflationFactorForSuggestion } from './estimationService';

/**
 * Index the rows once per recalculation:
 *  - indexByCode:    eBKP code -> first row index (mirrors the old findIndex)
 *  - childrenByCode: parent code -> direct child indices, in row order
 *                    (mirrors the old filter over parentPath)
 */
export const buildRowIndex = (rows) => {
  const indexByCode = new Map();
  const childrenByCode = new Map();

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    if (!indexByCode.has(row.ebkpCode)) {
      indexByCode.set(row.ebkpCode, i);
    }

    const path = row.parentPath;
    if (path && path.length > 0) {
      const parentCode = path[path.length - 1];
      const bucket = childrenByCode.get(parentCode);
      if (bucket) {
        bucket.push(i);
      } else {
        childrenByCode.set(parentCode, [i]);
      }
    }
  }

  return { indexByCode, childrenByCode };
};

/**
 * Check if a row has been manually edited by the user
 */
export const isManuallyEdited = (row) => {
  const hasProjectSelected = row.selectedSuggestionIndex !== null &&
                             row.selectedSuggestionIndex !== undefined;
  const hasManualCostPerUnit = row.manualCostPerUnit !== null &&
                                row.manualCostPerUnit !== undefined &&
                                row.manualCostPerUnit !== 0;
  const hasManualAmount = row.manualAmount !== null &&
                         row.manualAmount !== undefined &&
                         row.manualAmount !== 0;
  return hasProjectSelected || hasManualCostPerUnit || hasManualAmount;
};

/** A row is a leaf when no other row lists it as its direct parent. */
export const isLeafRow = (row, index) => {
  if (!row || !row.ebkpCode) return true;
  return !index.childrenByCode.has(row.ebkpCode);
};

/** Direct children of a row, in row order. */
export const getDirectChildRows = (row, rows, index) => {
  if (!row || !row.ebkpCode) return [];
  const kids = index.childrenByCode.get(row.ebkpCode);
  if (!kids) return [];
  return kids.map((i) => rows[i]);
};

/**
 * All descendants of a row, as row indices. Replaces the old recursive
 * getAllDescendants; only used to cascade includedInTotal, where order does
 * not matter. The seen set guards against a malformed parentPath cycle.
 */
export const collectDescendantIndices = (startCode, rows, index) => {
  const out = [];
  const seen = new Set();
  const stack = (index.childrenByCode.get(startCode) || []).slice();

  while (stack.length > 0) {
    const i = stack.pop();
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);

    const kids = index.childrenByCode.get(rows[i].ebkpCode);
    if (kids) {
      for (let k = 0; k < kids.length; k++) stack.push(kids[k]);
    }
  }

  return out;
};

/**
 * Recalculate every row total hierarchically and flag inconsistencies.
 *
 * Returns a new array. Rows whose calculatedTotal and hasInconsistency are
 * unchanged are returned by reference, so the table can skip them.
 */
export const recalculateAllTotals = (rows) => {
  const n = rows.length;
  const index = buildRowIndex(rows);
  const { indexByCode, childrenByCode } = index;

  // Live totals, seeded from the current values. Parents read the children
  // that were already recomputed, exactly as the in-place version did.
  const totals = new Array(n);
  for (let i = 0; i < n; i++) totals[i] = rows[i].calculatedTotal;

  const childrenSum = (row) => {
    if (!row || !row.ebkpCode) return 0;
    const kids = childrenByCode.get(row.ebkpCode);
    if (!kids) return 0;

    let sum = 0;
    for (let k = 0; k < kids.length; k++) {
      const ci = kids[k];
      if (rows[ci].includedInTotal === true) {
        sum += (totals[ci] || 0);
      }
    }
    return sum;
  };

  const individualRowTotal = (row) => {
    if (row.level === 1) {
      return childrenSum(row);
    }

    if (isManuallyEdited(row)) {
      const amount = row.manualAmount !== null && row.manualAmount !== undefined ? row.manualAmount : 0;
      let costPerUnit = 0;

      if (row.manualCostPerUnit !== null && row.manualCostPerUnit !== undefined) {
        costPerUnit = row.manualCostPerUnit;
      } else if (row.selectedSuggestionIndex !== null &&
                 row.selectedSuggestionIndex !== undefined &&
                 row.suggestions &&
                 row.suggestions[row.selectedSuggestionIndex]) {
        // Search-derived price: indexed from the project's year to the latest data point
        const suggestion = row.suggestions[row.selectedSuggestionIndex];
        costPerUnit = (suggestion.costPerUnit || 0) * inflationFactorForSuggestion(row, suggestion);
      }

      return calculateRowTotal(amount, costPerUnit);
    }

    if (!isLeafRow(row, index)) {
      return childrenSum(row);
    }

    return 0;
  };

  // Deepest level first, so children are settled before their parents.
  // Array.prototype.sort is stable, so equal levels keep their row order —
  // the same traversal the previous sort produced.
  const order = new Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  order.sort((a, b) => rows[b].level - rows[a].level);

  for (let k = 0; k < n; k++) {
    const code = rows[order[k]].ebkpCode;
    const target = indexByCode.get(code);
    if (target !== undefined) {
      totals[target] = individualRowTotal(rows[target]);
    }
  }

  // Inconsistency: a parent that was edited by hand but no longer matches the
  // sum of its children. The parent and its direct children are flagged.
  const inconsistent = new Set();
  for (let i = 0; i < n; i++) {
    const row = rows[i];
    if (row.level > 1 && !isLeafRow(row, index) && isManuallyEdited(row)) {
      const sum = childrenSum(row);
      const manualTotal = totals[i] || 0;

      if (Math.abs(manualTotal - sum) > 0.01 && sum > 0) {
        inconsistent.add(row.ebkpCode);
        const kids = childrenByCode.get(row.ebkpCode) || [];
        for (let k = 0; k < kids.length; k++) {
          inconsistent.add(rows[kids[k]].ebkpCode);
        }
      }
    }
  }

  // Only rebuild the rows that actually changed.
  const result = new Array(n);
  for (let i = 0; i < n; i++) {
    const row = rows[i];
    const nextTotal = totals[i];
    const nextFlag = inconsistent.has(row.ebkpCode);

    if (Object.is(row.calculatedTotal, nextTotal) && row.hasInconsistency === nextFlag) {
      result[i] = row;
    } else {
      result[i] = { ...row, calculatedTotal: nextTotal, hasInconsistency: nextFlag };
    }
  }

  return result;
};

/**
 * Calculate grand totals
 */
export const calculateGrandTotals = (rows, inputs) => {
  let grandTotal = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.level === 1 && row.includedInTotal === true) {
      grandTotal += (row.calculatedTotal || 0);
    }
  }

  const perGF = inputs.gf > 0 ? grandTotal / inputs.gf : 0;
  const perGV = inputs.gv > 0 ? grandTotal / inputs.gv : 0;

  return { grandTotal, perGF, perGV };
};

// ---------------------------------------------------------------------------
// Collapsible hierarchy
//
// The table is built from the whole eBKP-H catalogue, so it is ~2200 rows for
// every estimation regardless of how much was saved. Rendering all of them is
// what made each edit cost seconds (measured: ~1.4 ms of React work per row,
// and it is React work, not layout). Showing levels 1-2 by default and the
// rest on demand takes the default view to ~87 rows.
//
// Calculations always run over the full row set; only rendering is reduced.
// ---------------------------------------------------------------------------

/** Levels shown before anything is expanded by hand. */
export const DEFAULT_EXPAND_LEVEL = 2;

/**
 * The codes that must be expanded for every row up to `level` to be visible:
 * every row shallower than `level` that actually has children.
 */
export const expandedCodesForLevel = (rows, level) => {
  const { childrenByCode } = buildRowIndex(rows);
  const codes = new Set();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.level < level && childrenByCode.has(row.ebkpCode)) {
      codes.add(row.ebkpCode);
    }
  }
  return codes;
};

/** Every code that has at least one child — i.e. everything that can expand. */
export const allExpandableCodes = (rows) => {
  const { childrenByCode } = buildRowIndex(rows);
  const codes = new Set();
  for (let i = 0; i < rows.length; i++) {
    if (childrenByCode.has(rows[i].ebkpCode)) codes.add(rows[i].ebkpCode);
  }
  return codes;
};

/**
 * Ancestor codes implied by an eBKP-H code itself: B01.05.100 -> B, B01, B01.05.
 * Used only to decide what is on screen. A few catalogue rows carry a
 * parentPath that skips the levels in between (B01.05.100 lists only ["B"],
 * because B01.05 does not exist as a row of its own), and taking that
 * literally would leave those rows permanently visible under a collapsed
 * group. Calculations keep using parentPath exactly as before.
 */
export const ancestorCodesFromCode = (code) => {
  const text = String(code || '');
  if (!text) return [];

  const parts = text.split('.');
  const head = parts[0];
  const letters = head.replace(/[0-9]/g, '');

  const out = [];
  if (letters && letters !== head) out.push(letters);   // A
  if (head) out.push(head);                             // A01
  for (let i = 1; i < parts.length; i++) {
    out.push(parts.slice(0, i + 1).join('.'));          // A01.01, A01.01.100
  }

  out.pop();                                            // drop the row itself
  return out;
};

/**
 * For each row, the ancestors that actually exist as rows and therefore have
 * an expander. Built once per row set.
 */
export const buildAncestorIndex = (rows) => {
  const known = new Set();
  for (let i = 0; i < rows.length; i++) known.add(rows[i].ebkpCode);

  const index = new Map();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const chain = [];
    const seen = new Set();

    const add = (code) => {
      if (code && code !== row.ebkpCode && known.has(code) && !seen.has(code)) {
        seen.add(code);
        chain.push(code);
      }
    };

    (row.parentPath || []).forEach(add);
    ancestorCodesFromCode(row.ebkpCode).forEach(add);

    index.set(row.ebkpCode, chain);
  }
  return index;
};

/** A row is shown when every one of its ancestors is expanded. */
export const isRowVisible = (row, expandedCodes, ancestorIndex) => {
  const chain = ancestorIndex
    ? ancestorIndex.get(row.ebkpCode)
    : (row.parentPath || []);
  if (!chain || chain.length === 0) return true;
  for (let i = 0; i < chain.length; i++) {
    if (!expandedCodes.has(chain[i])) return false;
  }
  return true;
};

/**
 * Roll up, for every row that has children, what its subtree contains. This is
 * what the collapsed row shows so the user can tell where to open without
 * hunting: how many positions carry reference data, how many are filled in,
 * and whether anything inside is flagged or locked.
 *
 * Computed deepest-level-first, so each parent reuses its children's totals.
 */
export const buildGroupSummaries = (rows) => {
  const { childrenByCode } = buildRowIndex(rows);
  const summaries = new Map();

  const order = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) order[i] = i;
  order.sort((a, b) => rows[b].level - rows[a].level);

  for (let k = 0; k < order.length; k++) {
    const row = rows[order[k]];
    const kids = childrenByCode.get(row.ebkpCode);
    if (!kids) continue;

    let positions = 0, withReference = 0, filled = 0;
    let warning = false, bookmarked = false;

    for (let c = 0; c < kids.length; c++) {
      const child = rows[kids[c]];
      const sub = summaries.get(child.ebkpCode);

      positions += 1 + (sub ? sub.positions : 0);
      withReference += ((child.suggestions && child.suggestions.length > 0) ? 1 : 0)
                     + (sub ? sub.withReference : 0);
      filled += (isManuallyEdited(child) ? 1 : 0) + (sub ? sub.filled : 0);
      warning = warning || child.hasInconsistency === true || (sub ? sub.warning : false);
      bookmarked = bookmarked || child.isBookmarked === true || (sub ? sub.bookmarked : false);
    }

    summaries.set(row.ebkpCode, { positions, withReference, filled, warning, bookmarked });
  }

  return summaries;
};

/**
 * Reduce the tree to the rows matching `predicate`, keeping their ancestors so
 * each hit still reads in context. While a search is active this replaces the
 * expand/collapse state.
 *
 * `limit` caps how many *hits* are included — ancestors do not count against
 * it. Without a cap, one character is enough to pull in almost the whole sheet
 * ("e" matches 96% of the rows), and every one of those rows has to be mounted
 * by React at ~4 ms each. Counting continues past the cap so the caller can
 * say how many hits there are in total.
 *
 * Returns { rows, matchCount, shownCount }.
 */
export const rowsMatching = (rows, predicate, ancestorIndex, limit) => {
  const keep = new Set();
  const cap = typeof limit === 'number' && limit > 0 ? limit : Infinity;

  let matchCount = 0;
  let shownCount = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!predicate(row)) continue;

    matchCount += 1;
    if (shownCount >= cap) continue;   // keep counting, stop rendering

    shownCount += 1;
    keep.add(row.ebkpCode);
    const chain = ancestorIndex
      ? (ancestorIndex.get(row.ebkpCode) || [])
      : (row.parentPath || []);
    for (let p = 0; p < chain.length; p++) keep.add(chain[p]);
  }

  return {
    rows: rows.filter((r) => keep.has(r.ebkpCode)),
    matchCount,
    shownCount
  };
};

/** Case-insensitive match on eBKP code or element name. */
export const matchesQuery = (row, query) => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return true;
  return String(row.ebkpCode || '').toLowerCase().includes(needle)
      || String(row.name || '').toLowerCase().includes(needle);
};
